import React, { useState } from 'react';
import { Dialog, DialogContent } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from 'sonner';
import axios from 'axios';
import { API, AuthContext, triggerHaptic } from '@/App';
import { FaWhatsapp, FaDumbbell, FaAppleAlt, FaChartLine } from 'react-icons/fa';

const LandingPage = () => {
  const { setUser } = React.useContext(AuthContext);
  const [showAuth, setShowAuth] = useState(false);
  const [loading, setLoading] = useState(false);
  const [selectedGender, setSelectedGender] = useState('female');
  const [selectedPackage, setSelectedPackage] = useState('training_only');
  const [wantsNutrition, setWantsNutrition] = useState(false);

  const handleRegister = async (e) => {
    e.preventDefault();
    setLoading(true);
    const formData = new FormData(e.target);
    
    const data = {
      email: formData.get('email'),
      password: formData.get('password'),
      first_name: formData.get('first_name'),
      last_name: formData.get('last_name'),
      height: parseFloat(formData.get('height')),
      weight: parseFloat(formData.get('weight')),
      previous_injuries: formData.get('previous_injuries') || null,
      gender: formData.get('gender'),
      package: formData.get('package'),
      body_measurements: {
        waist: parseFloat(formData.get('waist') || 0),
        thighs: parseFloat(formData.get('thighs') || 0)
      }
    };

    try {
      const response = await axios.post(`${API}/auth/register`, data);
      localStorage.setItem('token', response.data.token);
      setUser(response.data.user);
      triggerHaptic('success');
      toast.success('Uspešno ste se registrovali!');
    } catch (error) {
      triggerHaptic('heavy');
      toast.error(error.response?.data?.detail || 'Greška pri registraciji');
    }
    setLoading(false);
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    const formData = new FormData(e.target);
    
    const data = {
      email: formData.get('email'),
      password: formData.get('password')
    };

    try {
      const response = await axios.post(`${API}/auth/login`, data);
      localStorage.setItem('token', response.data.token);
      setUser(response.data.user);
      triggerHaptic('success');
      toast.success('Uspešno ste se prijavili!');
    } catch (error) {
      triggerHaptic('heavy');
      toast.error(error.response?.data?.detail || 'Greška pri prijavi');
    }
    setLoading(false);
  };

  return (
    <div className="min-h-screen bg-black text-white relative overflow-hidden">
      {/* Background Image */}
      <div 
        className="absolute inset-0 z-0 opacity-30"
        style={{
          backgroundImage: `url('/background.png')`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundRepeat: 'no-repeat'
        }}
      />

      {/* Content */}
      <div className="relative z-10">
        {/* Header */}
        <header className="container mx-auto px-4 py-6 flex justify-between items-center">
          <img 
            src="/logo.png" 
            alt="ReStart Fit Logo" 
            className="h-16"
          />
          <Button 
            data-testid="login-btn"
            onClick={() => setShowAuth(true)} 
            className="accent-bg text-black font-semibold px-8 py-6 rounded-full btn-hover text-lg"
          >
            Uloguj se
          </Button>
        </header>

        {/* Hero Section */}
        <section className="container mx-auto px-4 py-20 text-center">
          <h1 className="text-5xl sm:text-6xl lg:text-7xl font-black mb-6 leading-tight">
            <span className="block animate-fade-in-up">Tvoj cilj, Moj plan.</span>
            <span className="accent-text block animate-fade-in-up animate-subtle-glow animate-delay-200">Zajedno do uspeha</span>
          </h1>
          <p className="text-lg sm:text-xl text-gray-300 mb-12 max-w-2xl mx-auto animate-fade-in animate-delay-400">
            Personalizovani treninzi, ishrana i praćenje napretka. Sve na jednom mestu.
          </p>
          <Button 
            data-testid="register-btn"
            onClick={() => setShowAuth(true)} 
            className="accent-bg text-black font-bold px-12 py-8 rounded-full btn-hover text-xl animate-scale-in animate-delay-300"
          >
            Registruj se
          </Button>
        </section>

        {/* Features */}
        <section className="container mx-auto px-4 py-20">
          <div className="grid md:grid-cols-3 gap-8">
            <div className="glass-dark rounded-3xl p-8 text-center animate-slide-in-up animate-delay-100 hover:scale-105 transition-transform duration-300">
              <FaDumbbell className="text-6xl accent-text mx-auto mb-4 hover:rotate-12 transition-transform" />
              <h3 className="text-2xl font-bold mb-3">Personalizovani Treninzi</h3>
              <p className="text-gray-300">Pet dana nedeljno prilagođenih vežbi sa video demonstracijama</p>
            </div>
            <div className="glass-dark rounded-3xl p-8 text-center animate-slide-in-up animate-delay-200 hover:scale-105 transition-transform duration-300">
              <FaAppleAlt className="text-6xl accent-text mx-auto mb-4 hover:rotate-12 transition-transform" />
              <h3 className="text-2xl font-bold mb-3">Plan Ishrane</h3>
              <p className="text-gray-300">Praćenje kalorija i makronutrijenata sa dnevnim ciljevima</p>
            </div>
            <div className="glass-dark rounded-3xl p-8 text-center animate-slide-in-up animate-delay-300 hover:scale-105 transition-transform duration-300">
              <FaChartLine className="text-6xl accent-text mx-auto mb-4 hover:rotate-12 transition-transform" />
              <h3 className="text-2xl font-bold mb-3">Praćenje Napretka</h3>
              <p className="text-gray-300">Nedeljne statistike i merenja za vidljive rezultate</p>
            </div>
          </div>
        </section>

        {/* Pricing */}
        <section className="container mx-auto px-4 py-20">
          <h2 className="text-4xl font-black text-center mb-12">Cenovnik</h2>
          
          {/* Interactive Price Calculator */}
          <div className="max-w-2xl mx-auto glass-dark rounded-3xl p-8 border-2 border-[#36e19966] mb-8">
            <div className="space-y-6">
              {/* Gender Selection */}
              <div>
                <label className="text-white text-lg font-semibold mb-3 block">Odaberite pol:</label>
                <div className="grid grid-cols-2 gap-4">
                  <button
                    onClick={() => setSelectedGender('female')}
                    className={`p-4 rounded-xl font-bold text-lg transition-all ${
                      selectedGender === 'female'
                        ? 'accent-bg text-black'
                        : 'bg-gray-700 text-white hover:bg-gray-600'
                    }`}
                  >
                    Žena
                  </button>
                  <button
                    onClick={() => setSelectedGender('male')}
                    className={`p-4 rounded-xl font-bold text-lg transition-all ${
                      selectedGender === 'male'
                        ? 'accent-bg text-black'
                        : 'bg-gray-700 text-white hover:bg-gray-600'
                    }`}
                  >
                    Muškarac
                  </button>
                </div>
              </div>

              {/* Nutrition Add-on */}
              <div className="bg-gray-800 rounded-xl p-4 border-2 border-gray-600">
                <label className="flex items-center justify-between cursor-pointer">
                  <div className="flex-1">
                    <div className="text-white text-lg font-semibold">Želim plan ishrane</div>
                    <div className="text-gray-400 text-sm">Dodaj personalizovani plan ishrane (+€20.00)</div>
                  </div>
                  <input
                    type="checkbox"
                    checked={wantsNutrition}
                    onChange={(e) => {
                      setWantsNutrition(e.target.checked);
                      setSelectedPackage(e.target.checked ? 'training_nutrition' : 'training_only');
                    }}
                    className="w-6 h-6 accent-green-500"
                  />
                </label>
              </div>

              {/* Total Price Display */}
              <div className="border-t-2 border-gray-600 pt-6">
                {/* First Month Special */}
                <div className="bg-green-900/30 border-2 border-green-500 rounded-xl p-4 mb-4">
                  <p className="text-green-400 text-sm font-semibold mb-1">SPECIJALNA PONUDA - PRVI MESEC</p>
                  <p className="text-white text-5xl font-black">€30.00</p>
                  {wantsNutrition && (
                    <p className="text-gray-300 text-sm mt-2">+ €20.00 plan ishrane = <span className="font-bold text-green-400">€50.00</span></p>
                  )}
                  <p className="text-gray-400 text-xs mt-2">Za prvi mesec - i muškarci i žene</p>
                </div>
                
                {/* Regular Price After */}
                <div className="text-center">
                  <p className="text-gray-400 text-sm mb-2">Nakon prvog meseca:</p>
                  <div className="space-y-1">
                    <div className="flex justify-center items-center gap-2 text-gray-300 text-sm">
                      <span>Treninzi:</span>
                      <span className="font-bold">€{selectedGender === 'female' ? '49.99' : '59.99'}</span>
                    </div>
                    {wantsNutrition && (
                      <div className="flex justify-center items-center gap-2 text-gray-300 text-sm">
                        <span>Plan ishrane:</span>
                        <span className="font-bold">€20.00</span>
                      </div>
                    )}
                    <p className="text-white text-2xl font-bold mt-2">
                      €{(() => {
                        const basePrice = selectedGender === 'female' ? 49.99 : 59.99;
                        const totalPrice = wantsNutrition ? basePrice + 20 : basePrice;
                        return totalPrice.toFixed(2);
                      })()} / mesečno
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* WhatsApp Contact */}
        <section className="container mx-auto px-4 py-20 text-center">
          <Button 
            data-testid="whatsapp-contact-btn"
            onClick={() => window.open('https://wa.me/381691553565', '_blank')}
            className="bg-green-500 hover:bg-green-600 text-white font-bold px-12 py-8 rounded-full btn-hover text-xl flex items-center gap-3 mx-auto"
          >
            <FaWhatsapp className="text-3xl" />
            Kontaktiraj Trenera
          </Button>
        </section>
      </div>

      {/* Auth Dialog */}
      <Dialog open={showAuth} onOpenChange={setShowAuth}>
        <DialogContent className="bg-gradient-to-br from-gray-900 to-black border-2 border-[#36e199] text-white max-w-md sm:max-w-lg max-h-[90vh] overflow-y-auto" data-testid="auth-dialog">
          <Tabs defaultValue="login" className="w-full">
            <TabsList className="grid w-full grid-cols-2 bg-gray-800 p-1 rounded-xl mb-6">
              <TabsTrigger 
                value="login" 
                data-testid="login-tab"
                className="rounded-lg data-[state=active]:bg-[#36e199] data-[state=active]:text-black text-white font-semibold"
              >
                Prijava
              </TabsTrigger>
              <TabsTrigger 
                value="register" 
                data-testid="register-tab"
                className="rounded-lg data-[state=active]:bg-[#36e199] data-[state=active]:text-black text-white font-semibold"
              >
                Registracija
              </TabsTrigger>
            </TabsList>
            
            <TabsContent value="login" className="mt-0">
              <div className="text-center mb-6">
                <h2 className="text-2xl font-bold text-white mb-2">Dobrodošli nazad!</h2>
                <p className="text-gray-400">Prijavite se na Vaš nalog</p>
              </div>
              <form onSubmit={handleLogin} className="space-y-5" data-testid="login-form">
                <div className="space-y-2">
                  <Label htmlFor="login-email" className="text-gray-200 text-base font-medium">Email adresa</Label>
                  <Input 
                    id="login-email" 
                    name="email" 
                    type="email" 
                    required 
                    placeholder="vasa@email.com"
                    className="bg-gray-800 border-gray-600 text-white h-12 text-base focus:border-[#36e199] focus:ring-[#36e199]" 
                    data-testid="login-email" 
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="login-password" className="text-gray-200 text-base font-medium">Lozinka</Label>
                  <Input 
                    id="login-password" 
                    name="password" 
                    type="password" 
                    required 
                    placeholder="••••••••"
                    className="bg-gray-800 border-gray-600 text-white h-12 text-base focus:border-[#36e199] focus:ring-[#36e199]" 
                    data-testid="login-password" 
                  />
                </div>
                <Button 
                  type="submit" 
                  disabled={loading} 
                  className="w-full accent-bg text-black font-bold h-12 text-base rounded-xl hover:opacity-90 transition-opacity" 
                  data-testid="login-submit-btn"
                >
                  {loading ? 'Učitavanje...' : 'Prijavi se'}
                </Button>
              </form>
            </TabsContent>
            
            <TabsContent value="register" className="mt-0">
              <div className="text-center mb-6">
                <h2 className="text-2xl font-bold text-white mb-2">Započnite transformaciju</h2>
                <p className="text-gray-400">Kreirajte nalog i započnite svoj put</p>
              </div>
              <form onSubmit={handleRegister} className="space-y-4" data-testid="register-form">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div className="space-y-2">
                    <Label htmlFor="first_name" className="text-gray-200 text-sm font-medium">Ime</Label>
                    <Input 
                      id="first_name" 
                      name="first_name" 
                      required 
                      placeholder="Marko"
                      className="bg-gray-800 border-gray-600 text-white h-11 focus:border-[#36e199] focus:ring-[#36e199]" 
                      data-testid="register-first-name" 
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="last_name" className="text-gray-200 text-sm font-medium">Prezime</Label>
                    <Input 
                      id="last_name" 
                      name="last_name" 
                      required 
                      placeholder="Petrović"
                      className="bg-gray-800 border-gray-600 text-white h-11 focus:border-[#36e199] focus:ring-[#36e199]" 
                      data-testid="register-last-name" 
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="email" className="text-gray-200 text-sm font-medium">Email adresa</Label>
                  <Input 
                    id="email" 
                    name="email" 
                    type="email" 
                    required 
                    placeholder="vasa@email.com"
                    className="bg-gray-800 border-gray-600 text-white h-11 focus:border-[#36e199] focus:ring-[#36e199]" 
                    data-testid="register-email" 
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="password" className="text-gray-200 text-sm font-medium">Lozinka</Label>
                  <Input 
                    id="password" 
                    name="password" 
                    type="password" 
                    required 
                    placeholder="••••••••"
                    className="bg-gray-800 border-gray-600 text-white h-11 focus:border-[#36e199] focus:ring-[#36e199]" 
                    data-testid="register-password" 
                  />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-2">
                    <Label htmlFor="height" className="text-gray-200 text-sm font-medium">Visina (cm)</Label>
                    <Input 
                      id="height" 
                      name="height" 
                      type="number" 
                      step="0.1" 
                      required 
                      placeholder="175"
                      className="bg-gray-800 border-gray-600 text-white h-11 focus:border-[#36e199] focus:ring-[#36e199]" 
                      data-testid="register-height" 
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="weight" className="text-gray-200 text-sm font-medium">Težina (kg)</Label>
                    <Input 
                      id="weight" 
                      name="weight" 
                      type="number" 
                      step="0.1" 
                      required 
                      placeholder="70"
                      className="bg-gray-800 border-gray-600 text-white h-11 focus:border-[#36e199] focus:ring-[#36e199]" 
                      data-testid="register-weight" 
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="gender" className="text-gray-200 text-sm font-medium">Pol</Label>
                  <select 
                    id="gender" 
                    name="gender" 
                    required 
                    value={selectedGender}
                    onChange={(e) => setSelectedGender(e.target.value)}
                    className="w-full bg-gray-800 border border-gray-600 rounded-md px-3 h-11 text-white focus:border-[#36e199] focus:ring-[#36e199] focus:outline-none" 
                    data-testid="register-gender"
                  >
                    <option value="female">Žena</option>
                    <option value="male">Muškarac</option>
                  </select>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-2">
                    <Label htmlFor="waist" className="text-gray-200 text-sm font-medium">Struk (cm)</Label>
                    <Input 
                      id="waist" 
                      name="waist" 
                      type="number" 
                      step="0.1" 
                      placeholder="80"
                      className="bg-gray-800 border-gray-600 text-white h-11 focus:border-[#36e199] focus:ring-[#36e199]" 
                      data-testid="register-waist" 
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="thighs" className="text-gray-200 text-sm font-medium">Butine (cm)</Label>
                    <Input 
                      id="thighs" 
                      name="thighs" 
                      type="number" 
                      step="0.1" 
                      placeholder="60"
                      className="bg-gray-800 border-gray-600 text-white h-11 focus:border-[#36e199] focus:ring-[#36e199]" 
                      data-testid="register-thighs" 
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="previous_injuries" className="text-gray-200 text-sm font-medium">Prethodne povrede (opcionalno)</Label>
                  <Textarea 
                    id="previous_injuries" 
                    name="previous_injuries" 
                    placeholder="Opišite prethodne povrede..."
                    className="bg-gray-800 border-gray-600 text-white focus:border-[#36e199] focus:ring-[#36e199]" 
                    rows={3} 
                    data-testid="register-injuries" 
                  />
                </div>
                
                {/* Nutrition Package Option */}
                <div className="bg-gray-800 rounded-xl p-4 border-2 border-gray-600">
                  <label className="flex items-start gap-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={wantsNutrition}
                      onChange={(e) => {
                        setWantsNutrition(e.target.checked);
                        setSelectedPackage(e.target.checked ? 'training_nutrition' : 'training_only');
                      }}
                      className="w-5 h-5 mt-0.5 accent-green-500"
                    />
                    <div className="flex-1">
                      <div className="text-white font-semibold">Želim plan ishrane (+€20.00)</div>
                      <div className="text-gray-400 text-sm">Dobićeš personalizovani plan ishrane za svaki dan nedelje</div>
                      <div className="text-[#36e199] text-sm font-bold mt-1">
                        Ukupno: €{(() => {
                          const basePrice = selectedGender === 'female' ? 49.99 : 59.99;
                          const totalPrice = wantsNutrition ? basePrice + 20 : basePrice;
                          return totalPrice.toFixed(2);
                        })()}
                      </div>
                    </div>
                  </label>
                </div>
                <input type="hidden" name="package" value={selectedPackage} />
                
                <Button 
                  type="submit" 
                  disabled={loading} 
                  className="w-full accent-bg text-black font-bold h-12 text-base rounded-xl hover:opacity-90 transition-opacity" 
                  data-testid="register-submit-btn"
                >
                  {loading ? 'Učitavanje...' : 'Registruj se'}
                </Button>
              </form>
            </TabsContent>
          </Tabs>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default LandingPage;
